import useTranslator from "@pma/hooks/use-translator"
import { useAuth } from "@pma/stores/auth-store"
import { DictType } from "@pma/types"
import { cn, computeBillTotal, getUserFromId } from "@pma/utils/helpers"
import { formatCurrency } from "@pma/utils/utils"
import { format } from "date-fns"
import { useMemo } from "react"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../common/table"
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table"
import { DataTableColumnHeader } from "../common/data-table/data-table-column-header"
import { Label } from "../common/label"

interface MainBoqInvoiceProps {
  bill: any
  mainDetails: any
}

const MainBoqInvoice = ({ bill, mainDetails }: MainBoqInvoiceProps) => {
  const { authConfig } = useAuth()
  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"
  const isSigned = mainDetails?.is_signed
  const isApproved = mainDetails?.is_approved
  const isDeclined = mainDetails?.is_declined
  const orgId = authConfig?.last_organization_id
  const isSignedAndApproved = isSigned && isApproved
  const organizationUsers = authConfig?.organizationUsers
  const availableWorkspaces = authConfig?.companies_of_user
  const currentOrg = availableWorkspaces?.find((org: DictType) => org?.id === orgId)

  const retentionAmount = bill?.retention_req || 0
  const subtotal = useMemo(() => {
    if (!mainDetails?._bill_items) return computeBillTotal(bill, false)
    if (isApproved || isSigned) {
      return mainDetails._bill_items
        .filter((item: any) => item?._boq_item?.is_item)
        .reduce((s: number, item: any) =>
          s + ((Number(item?.approved_amount) || 0) * (Number(item?._boq_item?.boq_cost) || 0)), 0)
    }
    return mainDetails._bill_items
      .filter((item: any) => item?._boq_item?.is_item)
      .reduce((s: number, item: any) =>
        s + ((Number(item?.amount) || 0) * (Number(item?._boq_item?.boq_cost) || 0)), 0)
  }, [mainDetails?._bill_items, isApproved, isSigned, bill])
  // currentBillTotal = sum of requested_sum / approved_sum (after discount)
  const currentBillTotal = useMemo(() => {
    if (!mainDetails?._bill_items) return computeBillTotal(bill, false)
    if (isApproved || isSigned) {
      return mainDetails._bill_items
        .filter((item: any) => item?._boq_item?.is_item)
        .reduce((s: number, item: any) => s + (Number(item?.approved_sum) || 0), 0)
    }
    return mainDetails._bill_items
      .filter((item: any) => item?._boq_item?.is_item)
      .reduce((s: number, item: any) => s + (Number(item?.requested_sum) || 0), 0)
  }, [mainDetails?._bill_items, isApproved, isSigned, bill])
  const discount = subtotal - currentBillTotal
  const vatPercent = mainDetails?.vat_percent ?? 18
  const taxTotal = (currentBillTotal - retentionAmount) * (vatPercent / 100)
  const billTotalAfterTax = (currentBillTotal - retentionAmount) + taxTotal

  const mergedBoqItems = useMemo(() => {
    if (!bill?.boq_items) return []
    if (!mainDetails?._bill_items) return bill.boq_items

    const billItemsMap = new Map(
      mainDetails._bill_items.map((billItem: any) => [billItem._boq_item?.id, billItem]),
    )

    // First pass: merge bill item data into is_item rows
    const merged = bill.boq_items.map((item: any) => {
      if (item?.is_item) {
        const billItem: any = billItemsMap.get(item.id)
        if (billItem) {
          return {
            ...item,
            amount: billItem?.amount ?? item?.amount,
            approved_amount: billItem?.approved_amount ?? item?.approved_amount,
            paid: billItem?.amount_by_signer ?? item?.paid,
            requested_sum: billItem?.requested_sum,
            approved_sum: billItem?.approved_sum,
            paid_sum: billItem?.paid_sum ?? item?.paid_sum,
            item_discount: billItem?.item_discount ?? item?.item_discount,
            discount: billItem?.discount ?? item?.discount,
            bill: billItem,
          }
        }
      }
      return item
    })

    // Second pass: compute paid and paid_sum for parent (non-item) rows from their children
    return merged.map((item: any) => {
      if (!item?.is_item && item?.number) {
        const prefix = item.number + "."
        const children = merged.filter(
          (child: any) => child?.is_item && child?.number?.startsWith(prefix),
        )
        if (children.length > 0) {
          const totalPaid = children.reduce((sum: number, c: any) => sum + (c?.paid || 0), 0)
          const totalPaidSum = children.reduce((sum: number, c: any) => sum + (c?.paid_sum || 0), 0)
          return { ...item, paid: totalPaid, paid_sum: totalPaidSum }
        }
      }
      return item
    })
  }, [bill?.boq_items, mainDetails?._bill_items])

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: "number",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={translate('Section')} className='' />
      ),
      cell: ({ row }: any) => {
        const item = row.original
        const itemNumber = item?.number
        return (
          <div className='flex gap-1'>
            <span className='text-xs sm:text-sm'>{itemNumber}</span>
          </div>
        )
      },
      enableSorting: false,
      size: 100,
    },
    {
      accessorKey: "description",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={translate('Description')} className='' />
      ),
      cell: ({ row }: any) => {
        const item = row.original
        const itemDescription = item?.description
        return (
          <div className='max-w-[200px] text-right text-xs leading-tight sm:max-w-xs sm:text-sm lg:min-w-[330px]'>
            {itemDescription}
          </div>
        )
      },
      size: 300,
      enableSorting: false,
    },
    {
      accessorKey: "units",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={translate('Unit')} className='text-center' />
      ),
      cell: ({ row }: any) => {
        const item = row.original
        const itemUnit = item?.units
        return <div className='text-center text-xs sm:text-sm'>{itemUnit}</div>
      },
      enableSorting: false,
    },
    {
      accessorKey: "boq_cost",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={translate('Price per unit')} className='' />
      ),
      cell: ({ row }: any) => {
        const item = row?.original
        return item?.is_item ? (
          <div className='min-w-fit text-center text-xs sm:text-sm'>
            <span className='flex items-center gap-1'>
              {formatCurrency(item?.discount_price, isHebrew)}
              <small className='text-[10px]'>
                ({Number(((item?.total_discount ?? 0) * 100).toFixed(2))}%)
              </small>{" "}
            </span>{" "}
            {item?.is_item && (
              <div className='mt-1 flex items-center justify-center gap-1'>
                <Label className='text-[10px]'>
                  {translate("Exclude Discount")}:{" "}
                  {item?.item_discount === 0 ? translate("True") : translate("False")}
                </Label>
              </div>
            )}
          </div>
        ) : null
      },
      size: 150,
      enableSorting: false,
    },
    {
      accessorKey: "amount",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={translate('Amount from estimate')} className='' />
      ),
      cell: ({ row }: any) => {
        const item = row?.original
        return (
          <div className='max-w-xs text-right text-xs leading-tight sm:text-sm'>
            {item?.amount || ""}
          </div>
        )
      },
      size: 100,
      enableSorting: false,
    },
    {
      accessorKey: "paid",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={translate('Accumulated Amount')} className='' />
      ),
      cell: ({ row }: any) => {
        const item = row?.original
        const accumulatedAmount = useMemo(() => {
          let totalAmount = 0
          if (["new", "pending"].includes(mainDetails?.status) && item?.is_item) {
            totalAmount += (item?.bill?.amount || 0) + (Number(item?.bill?._boq_item?.paid) || 0)
          } else if (mainDetails?.status === "approved" && item?.is_item) {
            totalAmount += (item?.bill?.approved_amount || 0) + (Number(item?.bill?._boq_item?.paid) || 0)
          } else if (mainDetails?.status === "signed" && item?.is_item) {
            totalAmount += Number(item?.bill?._boq_item?.paid) || 0
          }

          return totalAmount
        }, [item])
        return item?.is_item ? (
          <div className='text-center text-xs sm:text-sm'>{accumulatedAmount || 0}</div>
        ) : null
      },
      size: 50,
      enableSorting: false,
    },
    {
      accessorKey: "current_bill",
      header: ({ column }) => <DataTableColumnHeader column={column} title={translate('Current Bill')} />,
      cell: ({ row }: any) => {
        const item = row?.original
        return item?.is_item ? (
          <div className='flex min-w-[120px] flex-col text-center text-xs sm:min-w-fit sm:text-sm'>
            <span className='text-xs text-primary'>
              {translate("Requested")}: {item?.amount}
            </span>
            <span className='text-xs text-green-600'>
              {translate("Bill Approved")}: {item?.paid}
            </span>
          </div>
        ) : null
      },
      size: 100,
      enableSorting: false,
      enableHiding: true,
    },
    {
      accessorKey: "current_sum",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={translate('Current Sum')} className='' />
      ),
      cell: ({ row }: any) => {
        const item = row?.original
        return item?.is_item ? (
          <div className='flex flex-col items-center gap-1'>
            <span className='text-xs'>
              {translate("Requested")}:{" "}
              {formatCurrency(item?.requested_sum, isHebrew)}
            </span>
            <span className='text-xs text-green-600'>
              {translate(item?.is_signed ? "Signed" : "Approved")}:{" "}
              {formatCurrency(item?.approved_sum ?? item?.paid_sum, isHebrew)}
            </span>
          </div>
        ) : null
      },
      size: 200,
      enableSorting: false,
    },
    {
      accessorKey: "estimated_sum",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={translate('Boq sum')} className='' />
      ),
      cell: ({ row }: any) => {
        const item = row?.original
        return item?.is_item ? (
          <div className='text-center text-xs font-bold sm:text-sm'>
            <span className=''>{formatCurrency(item?.real_sum, isHebrew)}</span>
          </div>
        ) : null
      },
      enableSorting: false,
    },
    {
      accessorKey: "sum",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={translate('Accumulated Sum')} className='' />
      ),
      cell: ({ row }: any) => {
        const item = row?.original
        const accumulatedSum = useMemo(() => {
          let totalAmount = 0
          if (["new", "pending"].includes(mainDetails?.status) && item?.is_item) {
            totalAmount += (item?.bill?.requested_sum || 0) + (Number(item?.bill?._boq_item?.paid_sum) || 0)
          } else if (mainDetails?.status === "approved" && item?.is_item) {
            totalAmount += (item?.bill?.approved_sum || 0) + (Number(item?.bill?._boq_item?.paid_sum) || 0)
          } else if (mainDetails?.status === "signed" && item?.is_item) {
            totalAmount += Number(item?.bill?.paid_sum) || 0
          }

          return totalAmount
        }, [item])
        return item?.is_item ? (
          <div className='text-center text-xs sm:text-sm'>
            {formatCurrency(accumulatedSum || 0, isHebrew)}
          </div>
        ) : null
      },
      enableSorting: false,
    },
  ]

  const table = useReactTable({
    data: mergedBoqItems,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  const contractor: DictType = useMemo(() => {
    if (!!mainDetails?.user_id) {
      return getUserFromId(mainDetails?.user_id, organizationUsers)
    }
    return {}
  }, [mainDetails])

  return (
    <div className='print:p-6'>
      <div
        id={`bill-${bill?.number}`}
        className='w-full rounded-lg bg-white p-4 shadow-lg sm:p-6 md:p-0 print:p-0 print:shadow-none'
        dir={isHebrew ? "rtl" : "ltr"}
      >
        {/* Header Section */}
        <div className='mb-6 flex flex-col items-start justify-between gap-4 border-b-2 border-gray-200 pb-4 sm:flex-row'>
          <div className='flex w-full items-center gap-3 sm:w-auto'>
            {currentOrg?.company_logo && (
              <img
                src={currentOrg?.company_logo?.url || "/placeholder.svg"}
                alt={currentOrg?.organization_name}
                className='h-12 w-12 flex-shrink-0 rounded-lg bg-white object-contain p-2 sm:h-16 sm:w-16 md:h-20 md:w-20'
              />
            )}
            <div className='min-w-0'>
              <h1 className='mb-1 text-xl font-bold text-blue-600 sm:text-2xl md:text-3xl'>
                {translate("Entire Bill")}
              </h1>
              <div className='text-sm font-semibold text-gray-700 sm:text-base md:text-lg'>
                #{mainDetails?.number}
              </div>
            </div>
          </div>
          <div className={cn("w-full sm:w-auto", isHebrew ? "sm:text-left" : "sm:text-right")}>
            <h2 className='break-words text-base font-semibold text-gray-800 sm:text-lg md:text-xl'>
              {currentOrg?.organization_name}
            </h2>
          </div>
        </div>

        {/* Info Section */}
        <div className='mb-6 flex flex-col justify-between gap-6 lg:flex-row'>
          {/* Contractor Info */}
          <div className='flex-1'>
            <h3 className='mb-2 text-base font-semibold text-gray-800 sm:text-lg'>
              {translate("Contractor")}:
            </h3>
            <div className='space-y-1 text-sm text-gray-700 sm:text-base'>
              <p className='break-words font-medium'>{contractor?.name}</p>
              <p className='break-all'>{contractor?.phone}</p>
              <p className='break-all'>{contractor?.email}</p>
            </div>
          </div>
          {/* Date */}
          <div className={cn("flex-1", isHebrew ? "lg:text-left" : "lg:text-right")}>
            <div className='space-y-2 text-sm sm:text-base'>
              <div className='flex justify-between gap-4 lg:justify-end'>
                <span className='font-medium text-gray-700'>{translate("Date")}:</span>
                <span className='text-gray-600'>
                  {format(new Date(bill?.boq?.created_at), "P")}
                </span>
              </div>
              <div className='flex justify-between gap-4 lg:justify-end'>
                <span className='font-medium text-gray-700'>{translate("Status")}:</span>
                <span
                  className='font-semibold'
                  style={{
                    color: isDeclined ? "red" : isSignedAndApproved ? "green" : "gray",
                  }}
                >
                  {isApproved && !isSigned && translate("Approved")}
                  {isDeclined && translate("Declined")}
                  {isSignedAndApproved && translate("Paid")}
                  {!isSignedAndApproved &&
                    !isDeclined &&
                    !isApproved &&
                    translate("Pending Approval")}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className='overflow-x-auto rounded-md border'>
          <Table className='text-xs sm:text-sm'>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className='border-b bg-gray-50'>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className={cn(
                        "border-gray-200 px-1 py-2 text-center text-gray-900 sm:px-2 sm:py-3",
                        isHebrew ? "border-r first:border-r-0" : "border-l first:border-l-0",
                      )}
                      style={{ width: header.getSize() }}
                    >
                      {header.isPlaceholder ? null : (
                        <div className='flex items-center justify-center'>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </div>
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row, index) => {
                const rowData = row.original
                const isParent = rowData.hasChildren
                return (
                  <TableRow
                    key={row.id}
                    className={cn(
                      `border-b hover:bg-gray-50 ${
                        index % 2 === 0 ? "bg-white" : "bg-gray-25"
                      } ${isParent ? "font-medium" : ""}`,
                      rowData?.bill?.amount && "border-y border-primary bg-primary/5",
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          "border-gray-200 px-1 py-1.5 sm:px-2 sm:py-2",
                          isHebrew ? "border-r first:border-r-0" : "border-l first:border-l-0",
                        )}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>

        {/* Totals Section */}
        <div className='mb-6 flex justify-end'>
          <div className='w-full space-y-3 text-sm sm:w-80 sm:text-base'>
            <div className='flex justify-between border-b border-gray-200 py-2'>
              <strong className='text-gray-700'>{translate("subtotal")}:</strong>
              <span className='font-medium text-gray-700'>
                {formatCurrency(subtotal, isHebrew)}
              </span>
            </div>
            {discount > 0 && (
              <div className='flex justify-between border-b border-gray-200 py-2'>
                <strong className='text-gray-700'>{translate("Discount")} (-):</strong>
                <span className='font-medium text-red-600'>
                  -{formatCurrency(discount, isHebrew)}
                </span>
              </div>
            )}
            <div className='flex justify-between border-b border-gray-200 py-2'>
              <strong className='text-gray-700'>{translate("total after discount")}:</strong>
              <span className='font-medium text-gray-700'>
                {formatCurrency(currentBillTotal, isHebrew)}
              </span>
            </div>
            <div className='flex justify-between border-b border-gray-200 py-2'>
              <strong className='text-gray-700'>{translate("Retention")} (-):</strong>
              <span className='font-medium text-gray-700'>
                {formatCurrency(bill?.retention_req, isHebrew)}
              </span>
            </div>
            <div className='flex justify-between border-b border-gray-200 py-2'>
              <strong className='text-gray-700'>{translate("Total after retention")}:</strong>
              <span className='font-medium text-gray-700'>
                {formatCurrency(currentBillTotal - retentionAmount, isHebrew)}
              </span>
            </div>
            <div className='flex justify-between border-gray-200 py-2'>
              <strong className='text-gray-700'>{translate("Tax")} ({vatPercent}%):</strong>
              <span className='font-medium text-gray-700'>
                {formatCurrency(taxTotal, isHebrew)}
              </span>
            </div>
            <div className='flex justify-between border-t-2 border-gray-300 py-3'>
              <strong className='text-base font-bold text-gray-900 sm:text-lg'>
                {translate("Total")}:
              </strong>
              <span className='text-base font-bold text-gray-900 sm:text-lg'>
                {formatCurrency(billTotalAfterTax, isHebrew)}
              </span>
            </div>
          </div>
        </div>
        {/* Footer */}
        <div className='mt-6 border-t pt-4 text-center text-xs text-gray-500 sm:text-sm'>
          <p>
            {translate("All Rights Reserved")} - {translate("Rog Tech Systems")} ©{" "}
            {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </div>
  )
}

export default MainBoqInvoice
